
import { useState, useEffect } from 'react';

// Map for different Unsplash queries per time slot with more diverse queries
const TIME_CONFIG: Record<
  string,
  { queries: string[]; label: string }
> = {
  morning: {
    queries: [
      'sunrise morning landscape golden hour bright fresh',
      'dawn daybreak mountain lake peaceful',
      'morning light forest meadow dew',
      'early morning city sunrise urban',
      'golden hour nature wildlife birds'
    ],
    label: "Morning",
  },
  afternoon: {
    queries: [
      'daylight landscape sunshine blue sky vibrant',
      'afternoon sun beach ocean waves',
      'midday mountain valley green',
      'sunny day park garden flowers',
      'bright daylight architecture modern'
    ],
    label: "Afternoon",
  },
  evening: {
    queries: [
      'sunset dusk golden hour landscape warm',
      'twilight evening sky purple orange',
      'dusk cityscape lights reflection',
      'evening calm lake sunset',
      'golden sunset field countryside'
    ],
    label: "Evening",
  },
  night: {
    queries: [
      'night landscape stars moonlight dark calm',
      'nighttime city lights urban glow',
      'starry night sky milky way',
      'moonlight forest peaceful quiet',
      'night ocean waves reflection moon'
    ],
    label: "Night",
  },
};

export interface UnsplashLocation {
  title?: string;
  name?: string;
  city?: string;
  country?: string;
}

export interface TimeSlotUnsplashImage {
  url: string;
  smallUrl?: string;
  photographer: string;
  photographerUrl: string;
  date: string;
  timeSlot: string;
  location?: UnsplashLocation;
  queryUsed?: string;
  debugInfo?: {
    totalResults: number;
    selectedIndex: number;
    hashSeed: string;
  };
}

const ACCESS_KEY = 'zevGYo64hCsPYqa7EMLzX_Ue3hRwlzfzBTZcE_EimZk';

function getCurrentTimeSlot(): keyof typeof TIME_CONFIG {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 16) return 'afternoon';
  if (hour >= 16 && hour < 20) return 'evening';
  return 'night';
}

function getTodayKey(slot: string, queryIndex: number) {
  const date = new Date().toISOString().split('T')[0];
  return `timedUnsplash-${date}-${slot}-${queryIndex}`;
}

// Improved hash function with better distribution
function hashStringToNumber(str: string): number {
  let hash = 0;
  if (str.length === 0) return hash;
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash);
}

// Get a different query each day to add more variation
function getDailyQuery(slot: keyof typeof TIME_CONFIG): string {
  const queries = TIME_CONFIG[slot].queries;
  const dateStr = new Date().toISOString().split('T')[0];
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
  const queryIndex = dayOfYear % queries.length;
  
  console.log(`[Background] Using query ${queryIndex} for ${slot} on day ${dayOfYear}`);
  return queries[queryIndex];
}

export function useTimeAwareUnsplashBackground() {
  const [backgroundInfo, setBackgroundInfo] = useState<TimeSlotUnsplashImage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchForSlot = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const slot = getCurrentTimeSlot();
        const query = getDailyQuery(slot);
        const queryIndex = TIME_CONFIG[slot].queries.indexOf(query);
        const cacheKey = getTodayKey(slot, queryIndex);
        
        console.log(`[Background] Fetching for slot: ${slot}, query: "${query}"`);

        // Check cache first
        const cachedItem = localStorage.getItem(cacheKey);
        if (cachedItem) {
          const cached = JSON.parse(cachedItem);
          console.log(`[Background] Using cached image for ${slot}:`, cached.debugInfo);
          setBackgroundInfo(cached);
          setIsLoading(false);
          return;
        }

        // Fetch more images for better variety (increased from 18 to 50)
        const response = await fetch(
          `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&orientation=landscape&per_page=50&page=1&client_id=${ACCESS_KEY}`
        );
        
        if (!response.ok) {
          throw new Error(`Unsplash API error: ${response.status}`);
        }
        
        const data = await response.json();

        if (!Array.isArray(data.results) || data.results.length === 0) {
          throw new Error(`No images found for query: "${query}"`);
        }

        // Enhanced deterministic selection with better distribution
        const dateStr = new Date().toISOString().split('T')[0];
        const hourStr = new Date().getHours().toString();
        const seed = `${dateStr}-${slot}-${hourStr}-${queryIndex}`;
        const hash = hashStringToNumber(seed);
        const idx = hash % data.results.length;
        const image = data.results[idx];

        console.log(`[Background] Selected image ${idx} of ${data.results.length} for seed "${seed}"`);

        const result: TimeSlotUnsplashImage = {
          url: image.urls.full,
          smallUrl: image.urls.regular,
          photographer: image.user.name,
          photographerUrl: image.user.links.html,
          date: dateStr,
          timeSlot: slot,
          queryUsed: query,
          location: image.location ? {
            title: image.location.title,
            name: image.location.name,
            city: image.location.city,
            country: image.location.country
          } : undefined,
          debugInfo: {
            totalResults: data.results.length,
            selectedIndex: idx,
            hashSeed: seed
          }
        };

        // Cache the result
        localStorage.setItem(cacheKey, JSON.stringify(result));
        setBackgroundInfo(result);
        
        console.log(`[Background] Cached new image for ${slot}:`, result.debugInfo);
      } catch (err: any) {
        console.error(`[Background] Error fetching image:`, err);
        setError(err?.message || "Error fetching image");
        
        // Enhanced fallback with time-based variation
        const fallbackImages = [
          '/lovable-uploads/8de45ed2-0cb9-4464-a640-d5513ab4c4c3.png',
          '/lovable-uploads/69d8478c-eb2f-4542-8353-09c3789bab30.png',
          '/lovable-uploads/ad659737-78ef-4877-a50e-6d7abab0770a.png'
        ];
        
        const fallbackIndex = new Date().getHours() % fallbackImages.length;
        
        setBackgroundInfo({
          url: fallbackImages[fallbackIndex],
          photographer: 'Default',
          photographerUrl: '#',
          date: new Date().toISOString().split('T')[0],
          timeSlot: getCurrentTimeSlot(),
          queryUsed: 'fallback',
          debugInfo: {
            totalResults: 0,
            selectedIndex: fallbackIndex,
            hashSeed: 'fallback'
          }
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchForSlot();

    // Check for time slot changes more frequently (every 5 minutes)
    const interval = setInterval(() => {
      const currentSlot = getCurrentTimeSlot();
      if (backgroundInfo && backgroundInfo.timeSlot !== currentSlot) {
        console.log(`[Background] Time slot changed from ${backgroundInfo.timeSlot} to ${currentSlot}`);
        fetchForSlot();
      }
    }, 1000 * 60 * 5); // Check every 5 minutes

    return () => clearInterval(interval);
  }, []); // Remove backgroundInfo dependency to prevent unnecessary refetches

  return { backgroundInfo, isLoading, error };
}
